import { useState, useCallback, useRef, useEffect } from 'react';

interface PerformanceMetrics {
  renderTime: number;
  memoryUsage: number;
  frameDrops: number;
  loadingPhases: Record<string, number>;
}

interface PDFCache {
  url: string;
  blob: Blob;
  timestamp: number;
  method: 'prefetch' | 'cache';
  size: number;
}

export const usePDFPerformance = () => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    renderTime: 0,
    memoryUsage: 0,
    frameDrops: 0,
    loadingPhases: {}
  });
  
  const [cache, setCache] = useState<Map<string, PDFCache>>(new Map());
  const phaseTimers = useRef<Map<string, number>>(new Map());
  const renderStartTime = useRef<number>(0);

  const startPhase = useCallback((phase: string) => {
    phaseTimers.current.set(phase, performance.now());
  }, []);

  const endPhase = useCallback((phase: string) => {
    const startTime = phaseTimers.current.get(phase);
    if (startTime) {
      const duration = performance.now() - startTime;
      setMetrics(prev => ({
        ...prev,
        loadingPhases: {
          ...prev.loadingPhases,
          [phase]: duration
        }
      }));
      phaseTimers.current.delete(phase);
    }
  }, []);

  const trackRenderStart = useCallback(() => {
    renderStartTime.current = performance.now();
  }, []);

  const trackRenderEnd = useCallback(() => {
    if (renderStartTime.current) {
      const renderTime = performance.now() - renderStartTime.current;
      setMetrics(prev => ({
        ...prev,
        renderTime
      }));
    }
  }, []);

  const getCachedPDF = useCallback((url: string): PDFCache | null => {
    const cached = cache.get(url);
    if (cached && Date.now() - cached.timestamp < 30 * 60 * 1000) { // 30 minute cache
      return cached;
    }
    return null;
  }, [cache]);

  const cachePDF = useCallback(async (url: string, method: 'prefetch' | 'cache' = 'cache') => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const cacheEntry: PDFCache = {
        url,
        blob,
        timestamp: Date.now(),
        method,
        size: blob.size
      };
      
      setCache(prev => new Map(prev.set(url, cacheEntry)));
      return cacheEntry;
    } catch (error) {
      console.error('Failed to cache PDF:', error);
      return null;
    }
  }, []);

  const preloadPDF = useCallback(async (url: string) => {
    return cachePDF(url, 'prefetch');
  }, [cachePDF]);

  const optimizePDFUrl = useCallback((url: string, method: 'quality' | 'performance' = 'performance') => {
    const cached = getCachedPDF(url);
    if (cached) {
      return URL.createObjectURL(cached.blob);
    }

    // Add optimization parameters based on method
    const urlObj = new URL(url);
    if (method === 'performance') {
      urlObj.searchParams.set('compress', 'true');
      urlObj.searchParams.set('quality', '85');
    }
    
    return urlObj.toString();
  }, [getCachedPDF]);

  const clearCache = useCallback(() => {
    // Clean up blob URLs
    cache.forEach(entry => {
      if (entry.blob) {
        URL.revokeObjectURL(URL.createObjectURL(entry.blob));
      }
    });
    setCache(new Map());
  }, [cache]);

  const getPerformanceReport = useCallback(() => {
    const totalCacheSize = Array.from(cache.values()).reduce((sum, entry) => sum + entry.size, 0);
    
    return {
      ...metrics,
      cacheSize: totalCacheSize,
      cacheEntries: cache.size,
      memoryUsage: (performance as any).memory?.usedJSHeapSize || 0
    };
  }, [metrics, cache]);

  // Memory monitoring
  useEffect(() => {
    const interval = setInterval(() => {
      if ((performance as any).memory) {
        const memoryUsage = (performance as any).memory.usedJSHeapSize;
        setMetrics(prev => ({
          ...prev,
          memoryUsage
        }));
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearCache();
    };
  }, [clearCache]);

  return {
    metrics,
    startPhase,
    endPhase,
    trackRenderStart,
    trackRenderEnd,
    getCachedPDF,
    cachePDF,
    preloadPDF,
    optimizePDFUrl,
    clearCache,
    getPerformanceReport
  };
};